import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';

import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';

import { StyledOcticon, Timeline } from '@primer/react';
import { ClockIcon } from '@primer/octicons-react';
import { VariableSizeList } from 'react-window';

import parseISO from 'date-fns/parseISO';

import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';


import VersionHistoryItem, { LoadingVersionItem } from './VersionHistoryItem';
import CollapsibleCardView from '../common/CardView/CollapsibleCardView';
import ErrorLabel from '../common/Labels/ErrorLabel';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import VersionIcon from '../common/icons/VersionIcon';
import VerticalGradientOverlay from '../common/overlays/VerticalGradientOverlay';

const sortVersionHistoryData = (versionHistory = []) => versionHistory
  ? reverse(sortBy(versionHistory, versionItem => parseISO(versionItem.createdAt)))
  : versionHistory;

const VersionHistoryItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <VersionHistoryItem {...props} />
  </div>
))

const LoadingVersionItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <LoadingVersionItem {...props} />
  </div>
))

const ActivityRow = ({ index, style, data }) => {
  const {
    modelID,
    sortedVersionHistory,
    listRef,
    rowHeights,
    isItemLoaded,
    showCondensed,
    pipelineID,
  } = data;
  const rowRef = useRef({});
  const versionValue = modelID ? sortedVersionHistory[index].modelVersion : sortedVersionHistory[index].version;

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);


  if (!isItemLoaded(index)) {
    return <LoadingVersionItemWithRef ref={rowRef} condensed={showCondensed} />
  }

  return (
    <div style={style}>
      <VersionHistoryItemWithRef
        ref={rowRef}
        key={versionValue}
        entry={sortedVersionHistory[index]}
        condensed={showCondensed}
        modelID={modelID}
        pipelineID={pipelineID}
      />
    </div>
  )
};

const ActivityTimeline = ({
  width,
  modelID,
  sortedVersionHistory,
  listRef,
  rowHeights,
  isItemLoaded,
  showCondensed,
  pipelineID,
  itemCount,
  onLoadMoreVersions,
  height,
  getRowHeight
}) => {
  if (!sortedVersionHistory?.length) {
    return null;
  }

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={onLoadMoreVersions}
      minimumBatchSize={5}
      threshold={5}
    >
      {({ onItemsRendered, ref }) => (
        <VariableSizeList
          ref={(list) => {
            // Pass List ref through to InfiniteLoader
            ref(list);

            // And store a copy for yourself.
            listRef.current = list;
          }}
          height={height}
          onItemsRendered={onItemsRendered}
          itemCount={sortedVersionHistory.length}
          estimatedItemSize={106}
          itemSize={getRowHeight}
          width={width}
          itemData={{
            modelID,
            sortedVersionHistory,
            listRef,
            rowHeights,
            isItemLoaded,
            showCondensed,
            pipelineID
          }}
        >
          {ActivityRow}
        </VariableSizeList>
      )}
    </InfiniteLoader>
  )
};

export const VersionHistoryTimeline = ({
  _className,
  versionHistoryError,
  areVersionsLoading,
  canLoadMoreVersions,
  onLoadMoreVersions,
  versionHistory,
  modelID,
  pipelineID,
  timelineTitle = 'Version History',
  height = 480,
  showCondensed = false,
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});

  const sortedVersionHistory = useMemo(() => {
    if (!versionHistory?.length) {
      return null;
    }

    return [
      ...sortVersionHistoryData(versionHistory),
    ];
  }, [versionHistory]);

  const versionHistoryLength = sortedVersionHistory?.length || 0;

  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = canLoadMoreVersions ? versionHistoryLength + 1 : versionHistoryLength;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = index => !canLoadMoreVersions || index < versionHistoryLength;

  const noDataMessage = versionHistoryError
    ? `There was an error retrieving version history${modelID ? ' for this model' : pipelineID ? ' for this pipeline' : ''}...`
    : `No versions to show${modelID ? ' for this model' : pipelineID ? ' for this pipeline' : ''}`;

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    return rowHeight || 106;
  }, [rowHeights]);

  if (areVersionsLoading) {
    return <LoadingSpinner animationKey="version-log-loader" useOverlay />;
  }

  return (
    <CollapsibleCardView
      disableFullHeight
      headerText={timelineTitle} icon={VersionIcon}
      fixedHeight="400px"
    >
      {!sortedVersionHistory?.length ? (
        <div className="p-4 flex-grow flex flex-col justify-center items-center">
          <StyledOcticon
            className="mb-2 text-brand"
            icon={ClockIcon}
            size={64}
          />
          <ErrorLabel>{noDataMessage}</ErrorLabel>
        </div>
      ) : (
        <div className="overflow-scroll px-6 flex-grow">
          {/* This zIndex override creates a stacking context, which prevents the Overlays's zIndex: 20 from appearing over root components. */}
          <Timeline sx={{ zIndex: 1 }}>
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
            <AutoSizer disableHeight>
              {({ width }) => (
                <ActivityTimeline
                  width={width}
                  modelID={modelID}
                  sortedVersionHistory={sortedVersionHistory}
                  listRef={listRef}
                  rowHeights={rowHeights}
                  isItemLoaded={isItemLoaded}
                  showCondensed={showCondensed}
                  pipelineID={pipelineID}
                  itemCount={itemCount}
                  onLoadMoreVersions={onLoadMoreVersions}
                  height={height}
                  getRowHeight={getRowHeight}
                />
              )}
            </AutoSizer>
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          </Timeline>
        </div>
      )}
    </CollapsibleCardView>
  );
};
export default VersionHistoryTimeline;
